import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import axios from "axios";
import { uploadSmart } from "@/lib/upload";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileChips, trFold, SearchClear, statusesForZone, ALL_STATUSES, statusColorCls, hubsForZone, UploadProgress, useSaleStatuses, saleStatusCls, OfferAmountCell, KpiCard, areaNum, fmtAmountTr, CUR_LABEL, BASKI_STAGES, stageBadgeCls } from "@/components/shared";
import { MultiFilter, InlineText, DesignCell, useHiddenCols, ColumnsMenu, AreaFilterButtons, ProducerCell, ProducerQuickAddDialog, ProducerEditDialog, ProductionStatusCell, ProductionStatusAddDialog, ContactCell, ContactQuickAddDialog, SaleStatusAddDialog, useColOrder, OrderedCells, useResizableCols, usePinnedCols } from "@/pages/admin/ProjectsPage";
import { toast } from "sonner";
import { LogOut, Search, ClipboardList, Plus, Upload, X, FileText, Image as ImageIcon, FolderOpen, Ruler, Box, Boxes, Undo2, ArrowUp, ArrowDown, ArrowUpDown, FileSpreadsheet, Trash2, LayoutDashboard, Settings, Factory, Wallet, TrendingUp, Pencil, CalendarClock, ListChecks, Camera, AlertTriangle, ShoppingCart } from "lucide-react";
import { isImpersonating, endImpersonation } from "@/lib/impersonate";

const PROJECT_TYPES = ["Fuar Standı", "Etkinlik", "Teknik"];
export const PROGRESS_ITEMS = [
  "Kurulum Başladı", "Zemin Atıldı", "Kablolama Projeye uygun", "Network Kabloları uygun",
  "Duvarlar Kurulmaya başladı", "Boya-Suntalam işleri başladı", "Aydınlatma montajı başladı",
  "Görsel-Reklam işleri başladı", "Mobilya teslimatı yapıldı", "Stand Temizliği Başladı",
  "Teslim Kağıdı İmzalandı", "Stand Teslim Edildi",
];
export const progressPct = (p) => Math.round(((p.kurulum_progress || []).length / PROGRESS_ITEMS.length) * 100);
const zoneCls = {
  "Blue Zone": "bg-blue-500/10 text-blue-600 border-blue-500/20",
  "Green Zone": "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
};

const PickedFiles = ({ picked, onRemove }) =>
  picked.length === 0 ? null : (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="picked-files">
      {picked.map((pf, i) => (
        <div key={i} className="relative border border-slate-200 bg-slate-50 group" data-testid={`picked-file-${i}`}>
          {pf.url ? (
            <img src={pf.url} alt={pf.file.name} className="w-full h-20 object-cover" />
          ) : (
            <div className="w-full h-20 flex flex-col items-center justify-center gap-1 text-slate-400">
              <FileText size={18} />
            </div>
          )}
          <div className="px-1.5 py-1 text-[10px] text-slate-500 truncate">{pf.file.name}</div>
          <button
            type="button"
            onClick={() => onRemove(i)}
            data-testid={`picked-file-remove-${i}`}
            className="absolute top-1 right-1 bg-white/90 border border-slate-200 p-0.5 text-slate-500 hover:text-red-600 transition-colors duration-150"
          >
            <X size={11} />
          </button>
        </div>
      ))}
    </div>
  );

const usePicker = () => {
  const [picked, setPicked] = useState([]);
  const add = (fileList) => {
    const arr = Array.from(fileList || []);
    setPicked((p) => [...p, ...arr.map((f) => ({ file: f, url: f.type.startsWith("image/") ? URL.createObjectURL(f) : null }))]);
  };
  const remove = (i) => setPicked((p) => { if (p[i]?.url) URL.revokeObjectURL(p[i].url); return p.filter((_, j) => j !== i); });
  const clear = () => setPicked((p) => { p.forEach((x) => x.url && URL.revokeObjectURL(x.url)); return []; });
  return { picked, add, remove, clear };
};

const FilePickInput = ({ onPick, testid, label = "Dosya seç (çoklu)" }) => {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" multiple className="hidden" data-testid={testid} onChange={(e) => { onPick(e.target.files); e.target.value = ""; }} />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        className="w-full border border-dashed border-slate-300 bg-[#F5F7FA] hover:border-slate-400 transition-colors duration-200 p-4 flex items-center justify-center gap-2 text-slate-500 text-sm cursor-pointer"
      >
        <Upload size={15} /> {label}
      </button>
    </>
  );
};

export const KurulumDateCell = ({ p, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const save = () => {
    setEditing(false);
    const t = val.trim();
    if (t !== (p.kurulum_tarihi || "")) onSave(p, "kurulum_tarihi", t);
  };
  if (editing) {
    return (
      <Input
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder="12.10.2027"
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
        onBlur={save}
        data-testid={`assigner-kurulum-input-${p.id}`}
        className="rounded-none h-8 w-[110px] text-xs bg-white"
      />
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      {p.kurulum_tarihi ? (
        <span className="text-xs font-semibold tabular-nums" data-testid={`assigner-kurulum-${p.id}`}>{p.kurulum_tarihi}</span>
      ) : (
        <button type="button" onClick={() => { setVal(""); setEditing(true); }} data-testid={`assigner-kurulum-add-${p.id}`} className="text-xs text-slate-300 hover:text-slate-500 transition-colors duration-150">
          Tarih ekle
        </button>
      )}
      <button
        type="button"
        onClick={() => { setVal(p.kurulum_tarihi || ""); setEditing(true); }}
        data-testid={`assigner-kurulum-edit-${p.id}`}
        title="Tarihi düzenle"
        className="text-slate-300 hover:text-slate-500 transition-colors duration-150"
      >
        <Pencil size={12} />
      </button>
    </div>
  );
};

const ProgressCell = ({ p, onOpen }) => {
  const pct = progressPct(p);
  return (
    <button
      type="button"
      onClick={() => onOpen(p)}
      data-testid={`assigner-progress-btn-${p.id}`}
      title="Kurulum ilerlemesini işaretle"
      className="group/prog flex items-center gap-2 min-w-[96px]"
    >
      <div className="w-14 h-1.5 bg-slate-100 overflow-hidden">
        <div className={`h-full transition-all duration-300 ${pct === 100 ? "bg-emerald-500" : pct > 0 ? "bg-blue-500" : ""}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-semibold tabular-nums ${pct === 100 ? "text-emerald-600" : pct > 0 ? "text-slate-700" : "text-slate-300"} group-hover/prog:text-slate-900 transition-colors duration-150`}>
        %{pct}
      </span>
    </button>
  );
};

export default function AssignerPage({ publicMode = false }) {
  const auth = useAuth();
  const user = publicMode ? { contact_name: "Paylaşılan Görünüm", zones: ["Blue Zone", "Green Zone"], can_edit: false } : auth.user;
  const logout = auth.logout;
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [hubs, setHubs] = useState([]);
  const [q, setQ] = useState("");
  const [zoneSel, setZoneSel] = useState([]);
  const [hubSel, setHubSel] = useState([]);
  const [stSel, setStSel] = useState([]);
  const [saleSel, setSaleSel] = useState([]);
  const [standTypeSel, setStandTypeSel] = useState([]);
  const [producerSel, setProducerSel] = useState([]);
  const [prodStSel, setProdStSel] = useState([]);
  const [progressSel, setProgressSel] = useState([]);
  const [semSel, setSemSel] = useState([]);
  const [cmSel, setCmSel] = useState([]);
  const [areaSel, setAreaSel] = useState([]);
  const [producers, setProducers] = useState([]);
  const [prodAddFor, setProdAddFor] = useState(null);
  const [prodEditFor, setProdEditFor] = useState(null);
  const [prodStatuses, setProdStatuses] = useState([]);
  const [prodStatusAddFor, setProdStatusAddFor] = useState(null);
  const [progressFor, setProgressFor] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactAddFor, setContactAddFor] = useState(null);
  const [saleAddFor, setSaleAddFor] = useState(null);
  const [supAddFor, setSupAddFor] = useState(null);
  const [supplierStatuses, setSupplierStatuses] = useState([]);
  useEffect(() => {
    if (publicMode) return;
    api.get("/settings/supplier-statuses").then((r) => setSupplierStatuses(r.data.statuses || [])).catch(() => {});
  }, []);
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [hiddenCols, setHiddenCols] = useHiddenCols("cop31_assigner_hidden_cols");
  const canEdit = user?.can_edit !== false;
  const isMember = user?.account_kind === "restricted";
  const editableColsList = isMember && canEdit && Array.isArray(user?.editable_cols) ? user.editable_cols : null;
  const ce = (k) => canEdit && (!editableColsList || editableColsList.includes(k));
  const allowedColsList = Array.isArray(user?.visible_cols) && user.visible_cols.length > 0 ? user.visible_cols : null;
  const colAllowed = (k) => !allowedColsList || allowedColsList.includes(k);
  const v = (k) => colAllowed(k) && !hiddenCols.includes(k);
  const RO = ({ val, testid }) => <span data-testid={testid} className={`text-xs ${val ? "text-slate-700 font-medium" : "text-slate-300"}`}>{val || "—"}</span>;
  const cls = (k, base) => (v(k) ? base : `${base} hidden`);
  const saleStatuses = useSaleStatuses();
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);  const [progress, setProgress] = useState(null);
  const myZones = user?.zones || [];
  const canProdPrice = ["username", "contact_email", "email"].some((k) => (user?.[k] || "").toLowerCase() === "muratbucak@quattrostand.com");
  const [form, setForm] = useState({ name: "", zone: "", stand_no: "", project_type: "", area_m2: "", note: "", project_status: "", hub: "", sale_status: "" });
  const createPicker = usePicker();

  const load = () => (publicMode ? axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/projeler`) : api.get("/assigner/projects")).then((r) => { setRows(r.data); setLoaded(true); }).catch(() => setLoaded(true));
  useEffect(() => {
    load();
    if (publicMode) {
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/projeler/meta`).then((r) => {
        setHubs(r.data.hubs || []);
        setProducers(r.data.producers || []);
        setContacts(r.data.contacts || []);
        setProdStatuses(r.data.production_statuses || []);
        setSupplierStatuses(r.data.supplier_statuses || []);
      }).catch(() => {});
      return;
    }
    api.get("/settings/hubs").then((r) => setHubs(r.data.hubs || [])).catch(() => {});
    api.get("/assigner/producers").then((r) => setProducers(r.data || [])).catch(() => {});
    api.get("/assigner/contacts").then((r) => setContacts(r.data || [])).catch(() => {});
    api.get("/settings/production-statuses").then((r) => setProdStatuses(r.data.statuses || [])).catch(() => {});
  }, []);

  const setProductionStatus = async (p, val) => {
    try {
      await api.patch(`/assigner/projects/${p.id}`, { production_status: val });
      setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, production_status: val || null } : x)));
      toast.success(val ? `${p.name} → ${val}` : `${p.name}: üretim durumu kaldırıldı`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const assignProducer = async (p, producerId, nameHint) => {
    try {
      await api.patch(`/assigner/projects/${p.id}`, { producer_id: producerId });
      const pname = producerId ? (nameHint || producers.find((u) => u.id === producerId)?.name || producers.find((u) => u.id === producerId)?.contact_name || "Üretici") : null;
      setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, producer_id: producerId || null, producer_name: pname } : x)));
      toast.success(producerId ? `${p.name} → ${pname}` : `${p.name}: üretici kaldırıldı`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const assignContact = async (p, field, cid, newName) => {
    const nameKey = field === "sem_id" ? "sem_name" : "cm_name";
    try {
      await api.patch(`/assigner/projects/${p.id}`, { [field]: cid });
      const cname = newName || (cid ? contacts.find((c) => c.id === cid)?.name || null : null);
      setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, [field]: cid || null, [nameKey]: cname } : x)));
      toast.success(cid ? `${p.name} → ${cname}` : `${p.name}: atama kaldırıldı`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v, ...(k === "zone" ? { project_status: "", hub: "" } : {}) }));

  const toggleProgress = async (p, item) => {
    const cur = p.kurulum_progress || [];
    const next = cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item];
    const ordered = PROGRESS_ITEMS.filter((x) => next.includes(x));
    setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, kurulum_progress: ordered } : x)));
    setProgressFor((pf) => (pf && pf.id === p.id ? { ...pf, kurulum_progress: ordered } : pf));
    try {
      await api.patch(`/assigner/projects/${p.id}`, { kurulum_progress: ordered });
    } catch (e) {
      toast.error(apiError(e));
      setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, kurulum_progress: cur } : x)));
      setProgressFor((pf) => (pf && pf.id === p.id ? { ...pf, kurulum_progress: cur } : pf));
    }
  };

  const patchRow = async (p, key, v) => {
    const val = v === "__none__" ? "" : v;
    try {
      await api.patch(`/assigner/projects/${p.id}`, { [key]: val });
      setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, [key]: val || null } : x)));
      toast.success(val ? `${p.name} → ${val}` : `${p.name}: kaldırıldı`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const patchProducerPrice = async (p, payload) => {
    try {
      await api.patch(`/assigner/projects/${p.id}`, payload);
      setRows((rs) => rs.map((x) => (x.id === p.id ? {
        ...x,
        ...(payload.producer_price !== undefined ? { producer_price: payload.producer_price > 0 ? payload.producer_price : null } : {}),
        ...(payload.producer_price_currency !== undefined ? { producer_price_currency: payload.producer_price_currency } : {}),
      } : x)));
      toast.success(`${p.name}: üretici fiyatı güncellendi`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const patchOffer = async (p, payload) => {
    try {
      await api.patch(`/assigner/projects/${p.id}`, payload);
      setRows((rs) => rs.map((x) => (x.id === p.id ? {
        ...x,
        ...(payload.offer_amount !== undefined ? { offer_amount: payload.offer_amount > 0 ? payload.offer_amount : null } : {}),
        ...(payload.offer_currency !== undefined ? { offer_currency: payload.offer_currency } : {}),
      } : x)));
      toast.success(`${p.name}: teklif tutarı güncellendi`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteProject = async (p) => {
    if (!window.confirm(`"${p.name}" projesi silinsin mi? Bu işlem geri alınamaz.`)) return;
    try {
      await api.delete(`/assigner/projects/${p.id}`);
      setRows((rs) => rs.filter((x) => x.id !== p.id));
      toast.success("Proje silindi");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const patchField = async (p, payload, optimistic) => {
    try {
      await api.patch(`/assigner/projects/${p.id}`, payload);
      setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, ...optimistic } : x)));
      toast.success("Proje güncellendi");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitCreate = async (ev) => {
    ev.preventDefault();
    if (!form.name.trim()) return toast.error("Proje adı girin");
    if (!form.zone) return toast.error("Bölge seçin");
    if (!form.stand_no.trim()) return toast.error("Stand no girin");
    if (!form.project_type) return toast.error("Proje türü seçin");
    setBusy(true);
    try {
      let fileIds = [];
      if (createPicker.picked.length > 0) {
        const uploaded = await uploadSmart(createPicker.picked.map((x) => x.file), setProgress);
        fileIds = uploaded.map((f) => f.id);
      }
      await api.post("/assigner/projects", { ...form, file_ids: fileIds });
      toast.success("Proje oluşturuldu");
      setCreateOpen(false);
      createPicker.clear();
      setForm({ name: "", zone: "", stand_no: "", project_type: "", area_m2: "", note: "", project_status: "", hub: "", sale_status: "" });
      load();
    } catch (e) {
      toast.error(e?.message || apiError(e));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const zones = useMemo(() => [...new Set(rows.map((r) => r.zone))].sort(), [rows]);
  const term = trFold(q.trim());
  const tabBase = rows.filter((r) => !term || [r.name, r.stand_no, r.zone, r.project_type, r.hub].some((v) => trFold(v).includes(term)));
  const STAND_TYPES = ["Custom", "Template", "Customized Template"];
  const FM = {
    area: (p) => areaSel.length === 0 || areaSel.includes(areaNum(p.area_m2)),
    zone: (p) => zoneSel.length === 0 || zoneSel.includes(p.zone),
    hub: (p) => hubSel.length === 0 || hubSel.includes(p.hub || "__none__"),
    st: (p) => stSel.length === 0 || stSel.includes(p.project_status || "__none__"),
    sale: (p) => saleSel.length === 0 || saleSel.includes(p.sale_status || "__none__"),
    type: (p) => standTypeSel.length === 0 || standTypeSel.includes(p.stand_type || "__none__"),
    producer: (p) => producerSel.length === 0 || producerSel.includes(p.producer_id || "__none__"),
    prodSt: (p) => prodStSel.length === 0 || prodStSel.includes(p.production_status || "__none__"),
    progress: (p) => {
      if (progressSel.length === 0) return true;
      const n = (p.kurulum_progress || []).length;
      const b = n === 0 ? "none" : n === PROGRESS_ITEMS.length ? "done" : "inprog";
      return progressSel.includes(b);
    },
    sem: (p) => semSel.length === 0 || semSel.includes(p.sem_id || "__none__"),
    cm: (p) => cmSel.length === 0 || cmSel.includes(p.cm_id || "__none__"),
  };
  const baseFor = (except) => tabBase.filter((p) => Object.entries(FM).every(([k, fn]) => k === except || fn(p)));
  const areaFacet = baseFor("area");
  const areaOptions = [...new Set(areaFacet.map((p) => areaNum(p.area_m2)).filter((n) => n > 0))]
    .sort((a, b) => a - b)
    .map((n) => ({ value: n, count: areaFacet.filter((p) => areaNum(p.area_m2) === n).length }));
  const zoneFacet = baseFor("zone");
  const hubFacet = baseFor("hub");
  const stFacet = baseFor("st");
  const saleFacet = baseFor("sale");
  const typeFacet = baseFor("type");
  const producerFacet = baseFor("producer");
  const producerOpts = [...new Map(producerFacet.filter((p) => p.producer_id).map((p) => [p.producer_id, p.producer_name || "Üretici"])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1], "tr", { sensitivity: "base" }));
  const prodStFacet = baseFor("prodSt");
  const progressFacet = baseFor("progress");
  const progBucket = (p) => {
    const n = (p.kurulum_progress || []).length;
    return n === 0 ? "none" : n === PROGRESS_ITEMS.length ? "done" : "inprog";
  };
  const semFacet = baseFor("sem");
  const semOpts = [...new Map(semFacet.filter((p) => p.sem_id).map((p) => [p.sem_id, p.sem_name || "SEM"])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1], "tr", { sensitivity: "base" }));
  const cmFacet = baseFor("cm");
  const cmOpts = [...new Map(cmFacet.filter((p) => p.cm_id).map((p) => [p.cm_id, p.cm_name || "CM"])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1], "tr", { sensitivity: "base" }));
  const visible = baseFor(null);
  const hubOptions = zoneSel.length ? [...new Set(zoneSel.flatMap((z) => hubsForZone(hubs, z)))] : [...new Set(zones.flatMap((z) => hubsForZone(hubs, z)))];
  const statusOptions = [...new Set((zoneSel.length ? zoneSel : zones).flatMap((z) => statusesForZone(z)))];

  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const sortVal = (p) => {
    switch (sort.key) {
      case "zone": return p.zone || "";
      case "stand_no": return p.stand_no || "";
      case "area": return areaNum(p.area_m2);
      case "type": return p.project_type || "";
      case "stand_type": return p.stand_type || "";
      case "tasarim": return (p.files || []).length;
      case "uretim": return (p.uretim_files || []).length;
      case "baski": return (p.baski_files || []).length;
      case "elektrik": return (p.elektrik_files || []).length;
      case "baski_sureci": return BASKI_STAGES.indexOf(p.baski_sureci);
      case "icmal": return (p.icmal_files || []).length;
      case "status": return p.project_status || "";
      case "sale": return p.sale_status || "";
      case "sup_status": return p.supplier_status || "";
      case "hub": return p.hub || "";
      case "producer": return p.producer_name || "";
      case "sem": return p.sem_name || "";
      case "cm": return p.cm_name || "";
      case "prod_status": return p.production_status || "";
      case "kurulum": return (p.kurulum_tarihi || "").split(".").reverse().join("");
      case "progress": return (p.kurulum_progress || []).length;
      case "sorun": return p.open_issues || 0;
      case "winner": return p.winner?.company || "";
      case "prod_price": return p.producer_price ?? -1;
      case "offer": return p.offer_amount ?? -1;
      default: return p.name || "";
    }
  };
  const sorted = [...visible].sort((a, b) => {
    const va = sortVal(a), vb = sortVal(b);
    const cmp = typeof va === "number" ? va - vb : String(va).localeCompare(String(vb), "tr", { numeric: true, sensitivity: "base" });
    return sort.dir === "asc" ? cmp : -cmp;
  });
  const SortTh = ({ k, children, hide }) => hide ? null : (
    <th className="px-4 py-3 font-medium">
      <button
        type="button"
        onClick={() => toggleSort(k)}
        data-testid={`assigner-sort-${k}`}
        className={`inline-flex items-center gap-1 uppercase tracking-wider transition-colors duration-150 hover:text-slate-900 ${sort.key === k ? "text-slate-900" : ""}`}
      >
        {children}
        {sort.key === k ? (sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : <ArrowUpDown size={11} className="text-slate-300" />}
      </button>
    </th>
  );

  const ASSIGNER_COLS = [
    { key: "progress", label: "İlerleme" },
    ...(myZones.length > 1 ? [{ key: "zone", label: "Bölge" }] : []),
    { key: "stand_no", label: "Stand" }, { key: "area", label: "m²" },
    { key: "type", label: "Tür" }, { key: "stand_type", label: "Stand Türü" }, { key: "tasarim", label: "Tasarım" },
    { key: "uretim", label: "Üretim" }, { key: "baski", label: "Baskı" }, { key: "elektrik", label: "Elektrik" }, { key: "baski_sureci", label: "Baskı Süreci" },
    { key: "icmal", label: "İcmal" }, { key: "status", label: "Statü" }, { key: "hub", label: "Hub" }, { key: "producer", label: "Üretici" }, { key: "sem", label: "SEM" }, { key: "cm", label: "CM" }, { key: "prod_status", label: "Üretim Durumu" }, { key: "kurulum", label: "Kurulum Tarihi" }, { key: "sorun", label: "Açık Sorun" },
    { key: "sale", label: "Süreç Durum" }, { key: "sup_status", label: "Tedarikçi Süreç" }, { key: "offer", label: "Teklif Tutarı" }, { key: "winner", label: "Kazanan Tedarikçi" },
    ...(canProdPrice ? [{ key: "prod_price", label: "Üretici Fiyatı" }] : []),
  ];
  const [colOrder, setColOrder] = useColOrder("cop31_assigner_col_order_v2", ASSIGNER_COLS.map((c) => c.key));
  const tableRef = useRef(null);
  useResizableCols(tableRef, "cop31_assigner_col_widths");
  usePinnedCols(tableRef, "cop31_assigner_col_pins");

  const exportExcel = async () => {
    const XLSX = await import("xlsx");
    const data = sorted.map((p, i) => ({
      "#": i + 1,
      "Proje Adı": p.name || "",
      "Bölge": p.zone || "",
      "Stand No": p.stand_no || "",
      "Alan (m²)": areaNum(p.area_m2) || "",
      "Tür": p.project_type || "",
      "Stand Türü": p.stand_type || "",
      "Statü": p.project_status || "",
      "Hub": p.hub || "",
      "Üretici": p.producer_name || "",
      "SEM": p.sem_name || "",
      "CM": p.cm_name || "",
      "Üretim Durumu": p.production_status || "",
      "Kurulum Tarihi": p.kurulum_tarihi || "",
      "Kurulum İlerlemesi (%)": progressPct(p),
      "Açık Sorun": p.open_issues || 0,
      "Süreç Durum": p.sale_status || "",
      "Tedarikçi Süreç": p.supplier_status || "",
      "Teklif Tutarı": p.offer_amount != null ? `${Number(p.offer_amount).toLocaleString("tr-TR")} ${p.offer_currency || "TRY"}` : "",
      "Kazanan Tedarikçi": p.winner?.company || "",
      ...(canProdPrice ? { "Üretici Fiyatı": p.producer_price != null ? `${Number(p.producer_price).toLocaleString("tr-TR")} ${p.producer_price_currency || "TRY"}` : "" } : {}),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = [{ wch: 4 }, { wch: 40 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 20 }, { wch: 20 }, { wch: 22 }, { wch: 18 }, { wch: 28 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Projeler");
    XLSX.writeFile(wb, `projeler-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`${data.length} proje Excel'e aktarıldı`);
  };

  const formHubs = hubsForZone(hubs, form.zone);

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="assigner-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1700px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="font-heading text-xl font-extrabold tracking-tight">Gett<span className="text-slate-400">Proposal</span></div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold flex items-center gap-1"><ClipboardList size={10} /> Atayıcı</div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500 hidden sm:flex items-center gap-2" data-testid="assigner-name">
              {user?.contact_name}
              {myZones.map((z) => <Badge key={z} variant="outline" className={`rounded-none text-[10px] ${zoneCls[z] || ""}`}>{z}</Badge>)}
            </span>
            {!publicMode && isImpersonating() && (
              <button onClick={endImpersonation} data-testid="assigner-end-impersonation" className="inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600 hover:text-amber-700 transition-colors duration-200">
                <Undo2 size={14} /> Yönetici Hesabına Dön
              </button>
            )}
            {!publicMode && (
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="assigner-logout" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200">
              <LogOut size={14} /> Çıkış Yap
            </button>
            )}
          </div>
        </div>
      </header>
      <main className="max-w-[1700px] mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight">Projeler</h1>
            <p className="text-sm text-slate-500 mt-1">{myZones.join(" · ")} — {rows.length} proje</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Tedarikçi Dashboard geçici olarak gizlendi — geri açmak için "false &&" kısmını kaldırın */}
            {false && canProdPrice && (
              <Link to="/atayici/tedarikciler" data-testid="assigner-suppliers-link">
                <Button variant="outline" className="rounded-none h-10 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-2 transition-colors duration-200">
                  <Factory size={15} /> Tedarikçi
                </Button>
              </Link>
            )}
            {canEdit && !isMember && (
            <Link to="/atayici/ayarlar" data-testid="assigner-settings-link">
              <Button variant="outline" className="rounded-none h-10 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-2 transition-colors duration-200">
                <Settings size={15} /> Ayarlar
              </Button>
            </Link>
            )}
            {!publicMode && (
            <Link to="/atayici/dashboard" data-testid="assigner-dashboard-link">
              <Button variant="outline" className="rounded-none h-10 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-2 transition-colors duration-200">
                <LayoutDashboard size={15} /> Dashboard
              </Button>
            </Link>
            )}
            {!publicMode && !isMember && (
            <Link to="/atayici/setup-dashboard" data-testid="assigner-setup-dashboard-link">
              <Button variant="outline" className="rounded-none h-10 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-2 transition-colors duration-200">
                <ListChecks size={15} /> Setup Dashboard
              </Button>
            </Link>
            )}
            {!publicMode && (
            <Link to="/atayici/kurulum-takvimi" data-testid="assigner-kurulum-timeline-link">
              <Button variant="outline" className="rounded-none h-10 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-2 transition-colors duration-200">
                <CalendarClock size={15} /> Kurulum Takvimi
              </Button>
            </Link>
            )}
            {!publicMode && (
            <Link to="/saha-fotograflari" data-testid="assigner-field-photos-link">
              <Button variant="outline" className="rounded-none h-10 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-2 transition-colors duration-200">
                <Camera size={15} /> Saha Foto
              </Button>
            </Link>
            )}
            {!publicMode && (
            <Link to="/siparisler" data-testid="assigner-orders-link">
              <Button variant="outline" className="rounded-none h-10 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-2 transition-colors duration-200">
                <ShoppingCart size={15} /> Siparişler
              </Button>
            </Link>
            )}
            {canProdPrice && (
              <Link to="/atayici/odeme-plani" data-testid="assigner-payment-plan-link">
                <Button variant="outline" className="rounded-none h-10 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-2 transition-colors duration-200">
                  <Wallet size={15} /> Ödeme Planı
                </Button>
              </Link>
            )}
            {canProdPrice && (
              <Link to="/atayici/kar-zarar" data-testid="assigner-profit-analysis-link">
                <Button variant="outline" className="rounded-none h-10 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-2 transition-colors duration-200">
                  <TrendingUp size={15} /> Kar/Zarar Analizi
                </Button>
              </Link>
            )}
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Proje, stand ara..." data-testid="assigner-search" className="rounded-none border-slate-200 h-10 pl-9 pr-9 w-56 bg-white" />
              <SearchClear value={q} onClear={() => setQ("")} testid="assigner-search-clear" />
            </div>
            <ColumnsMenu columns={ASSIGNER_COLS.filter((c) => colAllowed(c.key))} hidden={hiddenCols} onChange={setHiddenCols} order={colOrder} onReorder={setColOrder} testid="assigner-columns-menu" />
            <Button variant="outline" onClick={exportExcel} data-testid="assigner-export-excel" className="rounded-none h-10 border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold gap-2 transition-colors duration-200">
              <FileSpreadsheet size={15} /> Excel
            </Button>
            {canEdit && !isMember && (
            <Button onClick={() => setCreateOpen(true)} data-testid="assigner-new-project-btn" className="rounded-none h-10 bg-slate-900 text-white hover:bg-slate-700 font-semibold gap-2 transition-colors duration-200">
              <Plus size={15} /> Yeni Proje
            </Button>
            )}
          </div>
        </div>

        <div className="flex items-end gap-3 flex-wrap" data-testid="assigner-filters">
          {zones.length > 1 && (
            <MultiFilter
              label="Bölge"
              testid="assigner-zone-select"
              totalCount={zoneFacet.length}
              selected={zoneSel}
              onChange={(v) => { setZoneSel(v); setHubSel([]); setStSel([]); }}
              options={zones.map((z) => ({ value: z, label: z, count: zoneFacet.filter((p) => p.zone === z).length }))}
            />
          )}
          {hubOptions.length > 0 && (
            <MultiFilter
              label="Hub"
              testid="assigner-hub-filter"
              width="w-[200px]"
              totalCount={hubFacet.length}
              selected={hubSel}
              onChange={setHubSel}
              options={[
                ...hubOptions.map((h) => ({ value: h, label: h, count: hubFacet.filter((p) => p.hub === h).length })),
                { value: "__none__", label: "Atanmamış", count: hubFacet.filter((p) => !p.hub).length },
              ]}
            />
          )}
          <MultiFilter
            label="Statü"
            testid="assigner-status-filter"
            width="w-[180px]"
            totalCount={stFacet.length}
            selected={stSel}
            onChange={setStSel}
            options={[
              ...statusOptions.map((st) => ({ value: st, label: st, count: stFacet.filter((p) => p.project_status === st).length })),
              { value: "__none__", label: "Atanmamış", count: stFacet.filter((p) => !p.project_status).length },
            ]}
          />
          <MultiFilter
            label="Süreç Durum"
            testid="assigner-sale-filter"
            width="w-[200px]"
            totalCount={saleFacet.length}
            selected={saleSel}
            onChange={setSaleSel}
            options={[
              ...saleStatuses.map((s) => ({ value: s, label: s, count: saleFacet.filter((p) => p.sale_status === s).length })),
              { value: "__none__", label: "Atanmamış", count: saleFacet.filter((p) => !p.sale_status).length },
            ]}
          />
          <MultiFilter
            label="Stand Türü"
            testid="assigner-stand-type-filter"
            totalCount={typeFacet.length}
            selected={standTypeSel}
            onChange={setStandTypeSel}
            options={[
              ...STAND_TYPES.map((t) => ({ value: t, label: t, count: typeFacet.filter((p) => p.stand_type === t).length })),
              { value: "__none__", label: "Atanmamış", count: typeFacet.filter((p) => !p.stand_type).length },
            ]}
          />
          <MultiFilter
            label="Üretici"
            testid="assigner-producer-filter"
            width="w-[190px]"
            totalCount={producerFacet.length}
            selected={producerSel}
            onChange={setProducerSel}
            options={[
              ...producerOpts.map(([id, name]) => ({ value: id, label: name, count: producerFacet.filter((p) => p.producer_id === id).length })),
              { value: "__none__", label: "Atanmamış", count: producerFacet.filter((p) => !p.producer_id).length },
            ]}
          />
          <MultiFilter
            label="Üretim Durumu"
            testid="assigner-prod-status-filter"
            width="w-[190px]"
            totalCount={prodStFacet.length}
            selected={prodStSel}
            onChange={setProdStSel}
            options={[
              ...prodStatuses.map((s) => ({ value: s, label: s, count: prodStFacet.filter((p) => p.production_status === s).length })),
              { value: "__none__", label: "Atanmamış", count: prodStFacet.filter((p) => !p.production_status).length },
            ]}
          />
          <MultiFilter
            label="İlerleme"
            testid="assigner-progress-filter"
            width="w-[190px]"
            totalCount={progressFacet.length}
            selected={progressSel}
            onChange={setProgressSel}
            options={[
              { value: "none", label: "Başlamadı (%0)", count: progressFacet.filter((p) => progBucket(p) === "none").length },
              { value: "inprog", label: "Devam Ediyor", count: progressFacet.filter((p) => progBucket(p) === "inprog").length },
              { value: "done", label: "Tamamlandı (%100)", count: progressFacet.filter((p) => progBucket(p) === "done").length },
            ]}
          />
          {semOpts.length > 0 && (
            <MultiFilter
              label="SEM"
              testid="assigner-sem-filter"
              width="w-[190px]"
              totalCount={semFacet.length}
              selected={semSel}
              onChange={setSemSel}
              options={[
                ...semOpts.map(([id, name]) => ({ value: id, label: name, count: semFacet.filter((p) => p.sem_id === id).length })),
                { value: "__none__", label: "Atanmamış", count: semFacet.filter((p) => !p.sem_id).length },
              ]}
            />
          )}
          {cmOpts.length > 0 && (
            <MultiFilter
              label="CM"
              testid="assigner-cm-filter"
              width="w-[190px]"
              totalCount={cmFacet.length}
              selected={cmSel}
              onChange={setCmSel}
              options={[
                ...cmOpts.map(([id, name]) => ({ value: id, label: name, count: cmFacet.filter((p) => p.cm_id === id).length })),
                { value: "__none__", label: "Atanmamış", count: cmFacet.filter((p) => !p.cm_id).length },
              ]}
            />
          )}
          {(q.trim() !== "" || zoneSel.length > 0 || hubSel.length > 0 || stSel.length > 0 || saleSel.length > 0 || standTypeSel.length > 0 || producerSel.length > 0 || prodStSel.length > 0 || progressSel.length > 0 || semSel.length > 0 || cmSel.length > 0 || areaSel.length > 0) && (
            <button
              onClick={() => { setQ(""); setZoneSel([]); setHubSel([]); setStSel([]); setSaleSel([]); setStandTypeSel([]); setProducerSel([]); setProdStSel([]); setProgressSel([]); setSemSel([]); setCmSel([]); setAreaSel([]); }}
              data-testid="assigner-filters-clear"
              className="h-9 px-3 text-xs font-semibold text-slate-500 hover:text-slate-900 border border-transparent hover:border-slate-300 transition-colors duration-200"
            >
              Filtreleri temizle
            </button>
          )}
        </div>

        <AreaFilterButtons options={areaOptions} selected={areaSel} onChange={setAreaSel} testid="assigner-area-filter" />

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="assigner-kpi-cards">
          <KpiCard icon={FolderOpen} label="Toplam Proje" value={visible.length} accent="bg-slate-900 text-white" testid="assigner-kpi-total" />
          <KpiCard icon={Ruler} label="Toplam m²" value={visible.reduce((s, p) => s + areaNum(p.area_m2), 0).toLocaleString("tr-TR", { maximumFractionDigits: 1 })} accent="bg-amber-500/10 text-amber-600" testid="assigner-kpi-area" />
          <KpiCard icon={Box} label="Özel Stand" value={visible.filter((p) => p.project_status === "Özel").length} accent="bg-emerald-500/10 text-emerald-600" testid="assigner-kpi-ozel" active={stSel.length === 1 && stSel[0] === "Özel"} onClick={() => setStSel(stSel.length === 1 && stSel[0] === "Özel" ? [] : ["Özel"])} />
          <KpiCard icon={Boxes} label="SBB" value={visible.filter((p) => p.project_status === "SBB").length} accent="bg-blue-500/10 text-blue-600" testid="assigner-kpi-sbb" active={stSel.length === 1 && stSel[0] === "SBB"} onClick={() => setStSel(stSel.length === 1 && stSel[0] === "SBB" ? [] : ["SBB"])} />
          <KpiCard
            icon={ListChecks}
            label="Kurulum İlerlemesi"
            value={(() => {
              const pct = visible.length ? (visible.reduce((s, p) => s + (p.kurulum_progress || []).length, 0) / (visible.length * PROGRESS_ITEMS.length)) * 100 : 0;
              return `%${pct > 0 && pct < 1 ? pct.toFixed(1).replace(".", ",") : Math.round(pct)}`;
            })()}
            accent="bg-sky-500/10 text-sky-600"
            testid="assigner-kpi-progress"
          />
        </div>

        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table ref={tableRef} className="w-full text-sm min-w-[1480px]">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-400">
                <th className="px-3 py-3 font-medium">#</th>
                <SortTh key="name" k="name">Proje</SortTh>
                <OrderedCells order={colOrder}>
                <SortTh key="zone" k="zone" hide={myZones.length <= 1 || !v("zone")}>Bölge</SortTh>
                <SortTh key="stand_no" k="stand_no" hide={!v("stand_no")}>Stand</SortTh>
                <SortTh key="area" k="area" hide={!v("area")}>m²</SortTh>
                <SortTh key="type" k="type" hide={!v("type")}>Tür</SortTh>
                <SortTh key="stand_type" k="stand_type" hide={!v("stand_type")}>Stand Türü</SortTh>
                <SortTh key="tasarim" k="tasarim" hide={!v("tasarim")}>Tasarım</SortTh>
                <SortTh key="uretim" k="uretim" hide={!v("uretim")}>Üretim</SortTh>
                <SortTh key="baski" k="baski" hide={!v("baski")}>Baskı</SortTh>
                <SortTh key="elektrik" k="elektrik" hide={!v("elektrik")}>Elektrik</SortTh>
                <SortTh key="baski_sureci" k="baski_sureci" hide={!v("baski_sureci")}>Baskı Süreci</SortTh>
                <SortTh key="icmal" k="icmal" hide={!v("icmal")}>İcmal</SortTh>
                <SortTh key="status" k="status" hide={!v("status")}>Statü</SortTh>
                <SortTh key="hub" k="hub" hide={!v("hub")}>Hub</SortTh>
                <SortTh key="producer" k="producer" hide={!v("producer")}>Üretici</SortTh>
                <SortTh key="sem" k="sem" hide={!v("sem")}>SEM</SortTh>
                <SortTh key="cm" k="cm" hide={!v("cm")}>CM</SortTh>
                <SortTh key="prod_status" k="prod_status" hide={!v("prod_status")}>Üretim Durumu</SortTh>
                <SortTh key="kurulum" k="kurulum" hide={!v("kurulum")}>Kurulum Tarihi</SortTh>
                <SortTh key="progress" k="progress" hide={!v("progress")}>İlerleme</SortTh>
                <SortTh key="sorun" k="sorun" hide={!v("sorun")}>Açık Sorun</SortTh>
                <SortTh key="sale" k="sale" hide={!v("sale")}>Süreç Durum</SortTh>
                <SortTh key="sup_status" k="sup_status" hide={!v("sup_status")}>Tedarikçi Süreç</SortTh>
                <SortTh key="offer" k="offer" hide={!v("offer")}>Teklif Tutarı</SortTh>
                <SortTh key="winner" k="winner" hide={!v("winner")}>Kazanan Tedarikçi</SortTh>
                {canProdPrice && <SortTh key="prod_price" k="prod_price" hide={!v("prod_price")}>Üretici Fiyatı</SortTh>}
                </OrderedCells>
                {canEdit && !isMember && <th className="px-4 py-3 font-medium text-right">İşlem</th>}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={(myZones.length > 1 ? 22 : 21) + (canProdPrice ? 1 : 0)} className="px-4 py-12 text-center text-slate-400" data-testid="assigner-empty">
                    {!loaded ? "Yükleniyor..." : term || zoneSel.length || hubSel.length || stSel.length || saleSel.length || standTypeSel.length || producerSel.length || prodStSel.length || semSel.length || cmSel.length || areaSel.length ? "Filtrenizle eşleşen sonuç yok." : "Bölgenizde henüz proje yok."}
                  </td>
                </tr>
              ) : (
                sorted.map((p, i) => (
                  <tr key={p.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors duration-150" data-testid={`assigner-row-${p.id}`}>
                    <td className="px-3 py-3 text-slate-400 tabular-nums text-xs">{i + 1}</td>
                    <td className="px-4 py-3 font-semibold min-w-[220px]">
                      {(canEdit && !editableColsList) ? <InlineText value={p.name} onSave={(v) => patchField(p, { name: v }, { name: v })} testid={`assigner-inline-name-${p.id}`} className="font-semibold" /> : <span className="font-semibold text-sm">{p.name}</span>}
                      {p.created_by_role === "assigner" && (
                        <Badge variant="outline" className="rounded-none text-[10px] mt-1 bg-violet-500/10 text-violet-600 border-violet-500/20">Ekleyen: {p.created_by_name}</Badge>
                      )}
                    </td>
                    <OrderedCells order={colOrder}>
                    {myZones.length > 1 && <td key="zone" className={cls("zone", "px-4 py-3")}><Badge variant="outline" className={`rounded-none text-xs ${zoneCls[p.zone] || "border-slate-300 text-slate-600"}`}>{p.zone}</Badge></td>}
                    <td key="stand_no" className={cls("stand_no", "px-4 py-3 text-slate-500")}>
                      {ce("stand_no") ? <InlineText value={p.stand_no || ""} onSave={(v) => patchField(p, { stand_no: v }, { stand_no: v })} testid={`assigner-inline-stand-${p.id}`} /> : <RO val={p.stand_no} />}
                    </td>
                    <td key="area" className={cls("area", "px-4 py-3 text-slate-500 tabular-nums")}>
                      {ce("area") ? <InlineText
                        value={p.area_m2 ? String(p.area_m2).replace(".", ",") : ""}
                        onSave={(v) => {
                          const n = parseFloat(String(v).replace(",", "."));
                          if (!Number.isFinite(n) || n < 0) { toast.error("Geçerli bir m² değeri girin"); return; }
                          patchField(p, { area_m2: n }, { area_m2: n });
                        }}
                        testid={`assigner-inline-area-${p.id}`}
                      /> : <RO val={p.area_m2 ? String(p.area_m2).replace(".", ",") : ""} />}
                    </td>
                    <td key="type" className={cls("type", "px-4 py-3")}>
                      {!ce("type") ? <RO val={p.project_type} /> : (
                      <Select value={p.project_type || ""} onValueChange={(v) => patchField(p, { project_type: v }, { project_type: v, ...(v !== "Fuar Standı" ? { stand_type: null } : {}) })}>
                        <SelectTrigger data-testid={`assigner-type-select-${p.id}`} className="rounded-none h-8 w-[118px] text-xs border-slate-200 bg-white text-slate-600 px-2">
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 rounded-none">
                          {PROJECT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      )}
                    </td>
                    <td key="stand_type" className={cls("stand_type", "px-4 py-3")}>
                      {!ce("stand_type") ? <RO val={p.project_type === "Fuar Standı" ? p.stand_type : ""} /> : p.project_type === "Fuar Standı" ? (
                        <Select value={p.stand_type || "__none__"} onValueChange={(v) => { const val = v === "__none__" ? "" : v; patchField(p, { stand_type: val }, { stand_type: val || null }); }}>
                          <SelectTrigger data-testid={`assigner-stand-type-select-${p.id}`} className={`rounded-none h-8 w-[150px] text-xs px-2 ${p.stand_type ? "bg-slate-100 text-slate-700 border-slate-300" : "bg-white text-slate-400 border-slate-200"}`}>
                            <SelectValue placeholder="Atanmamış" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-slate-200 rounded-none">
                            <SelectItem value="__none__">Atanmamış</SelectItem>
                            {STAND_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td key="tasarim" className={cls("tasarim", "px-4 py-3")}>
                      <DesignCell p={p} readOnly={!ce("tasarim")} endpoint={`/assigner/projects/${p.id}`} onChanged={(files) => setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, files } : x)))} />
                    </td>
                    <td key="uretim" className={cls("uretim", "px-4 py-3")}>
                      <DesignCell
                        p={p}
                        readOnly={!ce("uretim")}
                        endpoint={`/assigner/projects/${p.id}`}
                        filesKey="uretim_files"
                        addKey="add_uretim_file_ids"
                        removeKey="remove_uretim_file_ids"
                        idp="uretim"
                        label="Üretim"
                        pdfImagesOnly
                        onChanged={(uretim_files) => setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, uretim_files } : x)))}
                      />
                    </td>
                    <td key="baski" className={cls("baski", "px-4 py-3")}>
                      <DesignCell
                        p={p}
                        readOnly={!ce("baski")}
                        endpoint={`/assigner/projects/${p.id}`}
                        filesKey="baski_files"
                        addKey="add_baski_file_ids"
                        removeKey="remove_baski_file_ids"
                        idp="baski"
                        label="Baskı"
                        pdfImagesOnly
                        onChanged={(baski_files) => setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, baski_files } : x)))}
                      />
                    </td>
                    <td key="elektrik" className={cls("elektrik", "px-4 py-3")}>
                      <DesignCell
                        p={p}
                        readOnly={!ce("elektrik")}
                        endpoint={`/assigner/projects/${p.id}`}
                        filesKey="elektrik_files"
                        addKey="add_elektrik_file_ids"
                        removeKey="remove_elektrik_file_ids"
                        idp="elektrik"
                        label="Elektrik"
                        onChanged={(elektrik_files) => setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, elektrik_files } : x)))}
                      />
                    </td>
                    <td key="baski_sureci" className={cls("baski_sureci", "px-4 py-3")}>
                      {p.baski_sureci ? (
                        <span className={`inline-block text-[10px] font-bold border px-1.5 py-0.5 whitespace-nowrap ${stageBadgeCls(p.baski_sureci)}`} data-testid={`assigner-baski-sureci-${p.id}`}>{p.baski_sureci}</span>
                      ) : (
                        <span className="text-xs text-slate-300">—</span>
                      )}
                    </td>
                    <td key="icmal" className={cls("icmal", "px-4 py-3")}>
                      <DesignCell
                        p={p}
                        readOnly={!ce("icmal")}
                        endpoint={`/assigner/projects/${p.id}`}
                        filesKey="icmal_files"
                        addKey="add_icmal_file_ids"
                        removeKey="remove_icmal_file_ids"
                        idp="icmal"
                        label="İcmal"
                        pdfOnly
                        onChanged={(icmal_files) => setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, icmal_files } : x)))}
                      />
                    </td>
                    <td key="status" className={cls("status", "px-4 py-3")}>
                      {!ce("status") ? <RO val={p.project_status} testid={`assigner-status-ro-${p.id}`} /> : (
                      <Select value={p.project_status || "__none__"} onValueChange={(v) => patchRow(p, "project_status", v)}>
                        <SelectTrigger data-testid={`assigner-status-select-${p.id}`} className={`rounded-none h-8 w-[150px] text-xs font-semibold ${statusColorCls(p.project_status)}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 rounded-none">
                          <SelectItem value="__none__">Atanmamış</SelectItem>
                          {statusesForZone(p.zone).map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      )}
                    </td>
                    <td key="hub" className={cls("hub", "px-4 py-3")}>
                      {!ce("hub") ? <RO val={p.hub} /> : hubsForZone(hubs, p.zone).length === 0 && !p.hub ? (
                        <span className="text-slate-300 text-xs">—</span>
                      ) : (
                        <Select value={p.hub || "__none__"} onValueChange={(v) => patchRow(p, "hub", v)}>
                          <SelectTrigger data-testid={`assigner-hub-select-${p.id}`} className={`rounded-none h-8 w-[150px] text-xs font-semibold ${p.hub ? "border-slate-300 text-slate-700 bg-white" : "border-amber-300 text-amber-700 bg-amber-50"}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-slate-200 rounded-none">
                            <SelectItem value="__none__">Atanmamış</SelectItem>
                            {hubsForZone(hubs, p.zone).map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </td>
                    <td key="producer" className={cls("producer", "px-4 py-3")}>
                      {!ce("producer") ? <RO val={p.producer_name} /> : <ProducerCell p={p} producers={producers} onAssign={assignProducer} onAddNew={(proj) => setProdAddFor(proj)} onEdit={(pid) => { const u = producers.find((x) => x.id === pid); if (u) setProdEditFor(u); }} idp="assigner-producer" />}
                    </td>
                    <td key="sem" className={cls("sem", "px-4 py-3")}>
                      {!ce("sem") ? <RO val={p.sem_name} /> : <ContactCell p={p} field="sem_id" contacts={contacts} onAssign={assignContact} onAddNew={(proj, field) => setContactAddFor({ p: proj, field })} idp="assigner-sem" />}
                    </td>
                    <td key="cm" className={cls("cm", "px-4 py-3")}>
                      {!ce("cm") ? <RO val={p.cm_name} /> : <ContactCell p={p} field="cm_id" contacts={contacts} onAssign={assignContact} onAddNew={(proj, field) => setContactAddFor({ p: proj, field })} idp="assigner-cm" />}
                    </td>
                    <td key="prod_status" className={cls("prod_status", "px-4 py-3")}>
                      {!ce("prod_status") ? <RO val={p.production_status} /> : <ProductionStatusCell p={p} statuses={prodStatuses} onSave={setProductionStatus} onAddNew={(proj) => setProdStatusAddFor(proj)} idp="assigner-prod-status" />}
                    </td>
                    <td key="kurulum" className={cls("kurulum", "px-4 py-3")}>
                      {(!editableColsList || editableColsList.includes("kurulum")) ? <KurulumDateCell p={p} onSave={patchRow} /> : <span className="text-xs font-semibold tabular-nums">{p.kurulum_tarihi || "—"}</span>}
                    </td>
                    <td key="progress" className={cls("progress", "px-4 py-3")}>
                      <ProgressCell p={p} onOpen={(!editableColsList || editableColsList.includes("progress")) ? setProgressFor : () => {}} />
                    </td>
                    <td key="sorun" className={cls("sorun", "px-4 py-3")} data-testid={`assigner-issues-${p.id}`}>
                      {p.open_issues > 0 ? (
                        <button type="button" onClick={() => navigate(`/saha-fotograflari?project=${p.id}&sorun=1`)} title="Açık sorunları gör" className="inline-flex items-center gap-1 text-[11px] font-bold bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 hover:bg-red-100 transition-colors duration-150">
                          <AlertTriangle size={11} /> {p.open_issues}
                        </button>
                      ) : <span className="text-slate-300 text-xs">—</span>}
                    </td>
                    <td key="sale" className={cls("sale", "px-4 py-3")}>
                      {!ce("sale") ? <RO val={p.sale_status} /> : (
                      <Select value={p.sale_status || "__none__"} onValueChange={(v) => (v === "__add__" ? setSaleAddFor(p) : patchRow(p, "sale_status", v))}>
                        <SelectTrigger data-testid={`assigner-sale-select-${p.id}`} className={`rounded-none h-8 w-[170px] text-xs font-semibold ${saleStatusCls(p.sale_status)}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 rounded-none">
                          <SelectItem value="__none__">Atanmamış</SelectItem>
                          {saleStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          <SelectItem value="__add__" className="text-blue-600 font-semibold" data-testid={`assigner-sale-add-new-${p.id}`}>+ Ekle</SelectItem>
                        </SelectContent>
                      </Select>
                      )}
                    </td>
                    <td key="sup_status" className={cls("sup_status", "px-4 py-3")}>
                      {!ce("sup_status") ? <RO val={p.supplier_status} /> : (
                      <Select value={p.supplier_status || "__none__"} onValueChange={(v) => (v === "__add__" ? setSupAddFor(p) : patchRow(p, "supplier_status", v === "__none__" ? "" : v))}>
                        <SelectTrigger data-testid={`assigner-supstatus-select-${p.id}`} className={`rounded-none h-8 w-[170px] text-xs font-semibold ${saleStatusCls(p.supplier_status)}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200 rounded-none">
                          <SelectItem value="__none__">Atanmamış</SelectItem>
                          {supplierStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          <SelectItem value="__add__" className="text-blue-600 font-semibold" data-testid={`assigner-supstatus-add-new-${p.id}`}>+ Ekle</SelectItem>
                        </SelectContent>
                      </Select>
                      )}
                    </td>
                    <td key="offer" className={cls("offer", "px-4 py-3")}>
                      {!ce("offer") ? <RO val={p.offer_amount != null ? `${fmtAmountTr(p.offer_amount)} ${CUR_LABEL[p.offer_currency || "TRY"]}` : ""} /> : <OfferAmountCell p={p} onSave={patchOffer} />}
                    </td>
                    <td key="winner" className={cls("winner", "px-4 py-3")} data-testid={`assigner-winner-${p.id}`}>
                      {p.winner ? (
                        <span className="font-semibold text-emerald-700 text-xs">{p.winner.company}</span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    {canProdPrice && (
                      <td key="prod_price" className={cls("prod_price", "px-4 py-3")}>
                        <OfferAmountCell p={p} onSave={patchProducerPrice} amountKey="producer_price" currencyKey="producer_price_currency" idp="prod-price" />
                      </td>
                    )}
                    </OrderedCells>
                    {canEdit && !isMember && (
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => deleteProject(p)}
                        data-testid={`assigner-delete-${p.id}`}
                        title="Projeyi sil"
                        className="w-8 h-8 inline-flex items-center justify-center border border-slate-200 text-slate-400 hover:text-red-600 hover:bg-red-50 hover:border-red-200 transition-colors duration-150"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      <ProducerQuickAddDialog
        open={!!prodAddFor}
        onOpenChange={(o) => !o && setProdAddFor(null)}
        createEndpoint="/assigner/producers"
        onCreated={(u) => { setProducers((ps) => [...ps, u]); if (prodAddFor) assignProducer(prodAddFor, u.id, u.name || u.contact_name); setProdAddFor(null); }}
      />

      <ProducerEditDialog
        open={!!prodEditFor}
        onOpenChange={(o) => !o && setProdEditFor(null)}
        producer={prodEditFor}
        saveEndpoint="/assigner/producers"
        onSaved={(u) => { setProducers((ps) => ps.map((x) => (x.id === u.id ? u : x))); setRows((rs) => rs.map((x) => (x.producer_id === u.id ? { ...x, producer_name: u.name } : x))); setProdEditFor(null); toast.success("Üretici güncellendi"); }}
      />

      <Dialog open={!!progressFor} onOpenChange={(o) => !o && setProgressFor(null)}>
        <DialogContent className="rounded-none sm:max-w-md" data-testid="progress-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ListChecks size={18} /> {progressFor?.name}
            </DialogTitle>
          </DialogHeader>
          {progressFor && (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-slate-500">Kurulum İlerlemesi</span>
                  <span className={`font-bold tabular-nums ${progressPct(progressFor) === 100 ? "text-emerald-600" : "text-slate-900"}`} data-testid="progress-dialog-pct">
                    %{progressPct(progressFor)}
                  </span>
                </div>
                <div className="h-2 bg-slate-100 overflow-hidden">
                  <div className={`h-full transition-all duration-300 ${progressPct(progressFor) === 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${progressPct(progressFor)}%` }} />
                </div>
              </div>
              <div className="space-y-1 max-h-[55vh] overflow-y-auto">
                {PROGRESS_ITEMS.map((item, i) => {
                  const checked = (progressFor.kurulum_progress || []).includes(item);
                  return (
                    <label
                      key={item}
                      className={`flex items-center gap-3 px-3 py-2 border cursor-pointer transition-colors duration-150 ${checked ? "bg-emerald-50/60 border-emerald-200" : "bg-white border-slate-200 hover:border-slate-300"}`}
                      data-testid={`progress-item-${i}`}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggleProgress(progressFor, item)} className="rounded-none" data-testid={`progress-check-${i}`} />
                      <span className={`text-sm ${checked ? "text-slate-800 font-medium" : "text-slate-600"}`}>{item}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ProductionStatusAddDialog
        open={!!prodStatusAddFor}
        onOpenChange={(o) => !o && setProdStatusAddFor(null)}
        onCreated={(st, list) => { setProdStatuses(list); if (prodStatusAddFor) setProductionStatus(prodStatusAddFor, st); setProdStatusAddFor(null); }}
      />

      <ContactQuickAddDialog
        open={!!contactAddFor}
        onOpenChange={(o) => !o && setContactAddFor(null)}
        kind={contactAddFor?.field === "cm_id" ? "CM" : "SEM"}
        zone={contactAddFor?.p?.zone}
        createEndpoint="/assigner/contacts"
        onCreated={(c) => { setContacts((cs) => [...cs, c]); if (contactAddFor) assignContact(contactAddFor.p, contactAddFor.field, c.id, c.name); setContactAddFor(null); }}
      />

      <SaleStatusAddDialog
        open={!!saleAddFor}
        onOpenChange={(o) => !o && setSaleAddFor(null)}
        onCreated={(st) => { if (saleAddFor) patchRow(saleAddFor, "sale_status", st); setSaleAddFor(null); }}
      />

      <Dialog open={!!supAddFor} onOpenChange={(o) => !o && setSupAddFor(null)}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-sm" data-testid="supplier-status-add-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Yeni Tedarikçi Süreç Durumu Ekle</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const val = (e.target.elements.supst.value || "").trim();
              if (!val) return toast.error("Durum adı girin");
              try {
                const { data } = await api.post("/settings/supplier-statuses", { status: val });
                setSupplierStatuses(data.statuses || []);
                if (supAddFor) patchRow(supAddFor, "supplier_status", val);
                setSupAddFor(null);
                toast.success("Tedarikçi süreç durumu eklendi");
              } catch (err) {
                toast.error(apiError(err));
              }
            }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label>Durum Adı</Label>
              <Input name="supst" autoFocus placeholder="örn. Bakiye Ödendi" data-testid="supplier-status-add-input" className="rounded-none bg-[#F5F7FA] border-slate-200" />
            </div>
            <Button type="submit" data-testid="supplier-status-add-submit" className="w-full rounded-none bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
              Ekle
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) createPicker.clear(); }}>
        <DialogContent aria-describedby={undefined} className="bg-white border-slate-200 rounded-none max-w-xl max-h-[90vh] overflow-y-auto" data-testid="assigner-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight">Yeni Proje</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Proje Adı</Label>
              <Input value={form.name} onChange={(e) => setField("name", e.target.value)} data-testid="assigner-form-name" className="rounded-none bg-[#F5F7FA] border-slate-200" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Bölge</Label>
                <Select value={form.zone} onValueChange={(v) => setField("zone", v)}>
                  <SelectTrigger className="rounded-none bg-[#F5F7FA] border-slate-200" data-testid="assigner-form-zone"><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 rounded-none">
                    {myZones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Stand No</Label>
                <Input value={form.stand_no} onChange={(e) => setField("stand_no", e.target.value)} data-testid="assigner-form-stand" className="rounded-none bg-[#F5F7FA] border-slate-200" />
              </div>
              <div className="space-y-1.5">
                <Label>Proje Türü</Label>
                <Select value={form.project_type} onValueChange={(v) => setField("project_type", v)}>
                  <SelectTrigger className="rounded-none bg-[#F5F7FA] border-slate-200" data-testid="assigner-form-type"><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 rounded-none">
                    {PROJECT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Alan (m²) <span className="text-slate-400 font-normal">opsiyonel</span></Label>
                <Input value={form.area_m2} onChange={(e) => setField("area_m2", e.target.value)} data-testid="assigner-form-area" className="rounded-none bg-[#F5F7FA] border-slate-200" />
              </div>
              <div className="space-y-1.5">
                <Label>Statü <span className="text-slate-400 font-normal">opsiyonel</span></Label>
                <Select value={form.project_status} onValueChange={(v) => setField("project_status", v)} disabled={!form.zone}>
                  <SelectTrigger className="rounded-none bg-[#F5F7FA] border-slate-200" data-testid="assigner-form-status"><SelectValue placeholder={form.zone ? "Seçin" : "Önce bölge seçin"} /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 rounded-none">
                    {statusesForZone(form.zone || null).map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Durum <span className="text-slate-400 font-normal">opsiyonel</span></Label>
                <Select value={form.sale_status} onValueChange={(v) => setField("sale_status", v)}>
                  <SelectTrigger className="rounded-none bg-[#F5F7FA] border-slate-200" data-testid="assigner-form-sale"><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200 rounded-none">
                    {saleStatuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {formHubs.length > 0 && (
                <div className="space-y-1.5">
                  <Label>Hub <span className="text-slate-400 font-normal">opsiyonel</span></Label>
                  <Select value={form.hub} onValueChange={(v) => setField("hub", v)}>
                    <SelectTrigger className="rounded-none bg-[#F5F7FA] border-slate-200" data-testid="assigner-form-hub"><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 rounded-none">
                      {formHubs.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Not <span className="text-slate-400 font-normal">opsiyonel</span></Label>
              <Textarea value={form.note} onChange={(e) => setField("note", e.target.value)} data-testid="assigner-form-note" rows={2} className="rounded-none bg-[#F5F7FA] border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label>Dosyalar <span className="text-slate-400 font-normal">opsiyonel, çoklu</span></Label>
              <FilePickInput onPick={createPicker.add} testid="assigner-create-file-input" />
              <PickedFiles picked={createPicker.picked} onRemove={createPicker.remove} />
            </div>
            {progress != null && <UploadProgress progress={progress} />}
            <Button type="submit" disabled={busy} data-testid="assigner-create-submit" className="w-full rounded-none bg-slate-900 text-white hover:bg-slate-700 font-semibold transition-colors duration-200">
              {busy ? "Oluşturuluyor..." : "Projeyi Oluştur"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
